import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
	Alert,
	AlertColor,
	Box,
	IconButton,
	Link,
	Snackbar,
	SnackbarCloseReason,
	Typography
} from "@mui/material";
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export type NotificationActionLink = {
	label: string;
	href?: string;
	onClick?: () => void;
};

export type NotificationPayload = {
	message: string;
	severity?: AlertColor;
	autoHideDuration?: number;
	/** Optional action link rendered below the message (bold, underlined). */
	actionLink?: NotificationActionLink;
};

type NotificationContextModel = {
	showNotification: (payload: NotificationPayload) => void;
};

const NotificationContext = createContext<NotificationContextModel | undefined>(undefined);

type NotificationState = {
	open: boolean;
	message: string;
	severity: AlertColor;
	autoHideDuration: number;
	actionLink?: NotificationActionLink;
};

const defaultState: NotificationState = {
	open: false,
	message: "",
	severity: "success",
	autoHideDuration: 4000
};

type Props = {
	children: React.ReactNode;
};

function severityIcon(severity: AlertColor) {
	switch (severity) {
		case "success":
			return <CheckCircleOutlineIcon fontSize="small" />;
		case "error":
			return <ErrorOutlineIcon fontSize="small" />;
		case "warning":
			return <WarningAmberIcon fontSize="small" />;
		case "info":
			return <InfoOutlinedIcon fontSize="small" />;
	}
}

export function NotificationProvider({ children }: Props) {
	const [state, setState] = useState<NotificationState>(defaultState);

	const showNotification = useCallback((payload: NotificationPayload) => {
		setState({
			open: true,
			message: payload.message,
			severity: payload.severity ?? "success",
			autoHideDuration: payload.autoHideDuration ?? 4000,
			actionLink: payload.actionLink
		});
	}, []);

	const handleClose = useCallback(
		(_: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
			if (reason === "clickaway") {
				return;
			}
			setState((prev) => ({ ...prev, open: false }));
		},
		[]
	);

	const handleActionLinkClick = useCallback(() => {
		try {
			state.actionLink?.onClick?.();
		} finally {
			setState((prev) => ({ ...prev, open: false }));
		}
	}, [state.actionLink]);

	const contextValue = useMemo(() => ({ showNotification }), [showNotification]);

	return (
		<NotificationContext.Provider value={contextValue}>
			{children}
			<Snackbar
				open={state.open}
				autoHideDuration={state.autoHideDuration}
				onClose={handleClose}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				sx={{ mt: 2 }}
			>
				<Alert
					severity={state.severity}
					icon={severityIcon(state.severity)}
					action={
						<IconButton
							size="small"
							aria-label="close"
							onClick={(event) => handleClose(event)}
							sx={{ color: "inherit" }}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					}
					sx={{
						alignItems: "center",
						px: 2,
						gap: 1,
						borderRadius: 1,
						boxShadow: 1
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1.25,
							width: "100%",
							flexWrap: { xs: "wrap", lg: "nowrap" }
						}}
					>
						<Typography
							variant="text14"
							sx={{
								flex: 1,
								pr: 1
							}}
						>
							{state.message}
						</Typography>
						{state.actionLink && (
							<Box
								sx={{
									flex: "0 0 auto",
									width: { xs: "100%", lg: "auto" },
									display: "flex",
									justifyContent: "flex-start"
								}}
							>
								<Link
									component={state.actionLink.href ? "a" : "button"}
									href={state.actionLink.href}
									onClick={handleActionLinkClick}
									underline="always"
									variant="text14Bold"
									sx={{
										lineHeight: "20px",
										color: "inherit",
										cursor: "pointer",
										textAlign: "left",
										background: "none",
										border: "none",
										p: 0,
										whiteSpace: "nowrap"
									}}
								>
									{state.actionLink.label}
								</Link>
							</Box>
						)}
					</Box>
				</Alert>
			</Snackbar>
		</NotificationContext.Provider>
	);
}

export function useNotification() {
	const context = useContext(NotificationContext);

	if (!context) {
		throw new Error("useNotification must be used within NotificationProvider");
	}

	return context;
}
